export interface MetaConfig {
  title: string;
  description: string;
  keywords: string[];
  ogImage: string;
  twitterHandle: string;
  canonicalUrl?: string;
  type?: string;
  locale?: string;
  alternateLocales?: string[];
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  section?: string;
  schema?: object;
}

const BASE_URL = 'https://validea.app';

export const defaultMeta: MetaConfig = {
  title: 'Validea - Validá tus ideas antes de construirlas',
  description: 'Descubrí qué productos realmente interesan a tus usuarios con analítica avanzada, formularios inteligentes y priorización basada en datos reales. La plataforma líder en validación de ideas y productos.',
  keywords: [
    'validación de ideas',
    'MVP',
    'producto mínimo viable',
    'analítica',
    'feedback de usuarios',
    'desarrollo de productos',
    'startup',
    'emprendimiento',
    'innovación',
    'validación de mercado',
    'investigación de usuarios',
    'product market fit',
    'desarrollo ágil',
    'lean startup',
    'métricas de producto'
  ],
  ogImage: `${BASE_URL}/og-image.png`,
  twitterHandle: '@validea',
  canonicalUrl: BASE_URL,
  type: 'website',
  locale: 'es_AR',
  alternateLocales: ['es_UY', 'es_PY'],
  author: 'Validea',
  section: 'Tecnología',
  schema: {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Validea',
    description: 'Plataforma de validación de ideas y productos que ayuda a emprendedores y empresas a validar sus conceptos antes de invertir en desarrollo.',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock'
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.8',
      ratingCount: '150'
    }
  }
};

// Configuraciones específicas para diferentes tipos de páginas
export const pageMetas = {
  home: {
    title: 'Validea - Plataforma de Validación de Ideas y Productos',
    description: 'Validá tus ideas de negocio antes de construirlas. Obtené feedback real de usuarios, analítica avanzada y priorización inteligente para tu MVP.',
    type: 'website',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'Validea',
      url: BASE_URL,
      potentialAction: {
        '@type': 'SearchAction',
        target: `${BASE_URL}/search?q={search_term_string}`,
        'query-input': 'required name=search_term_string'
      }
    }
  },
  pricing: {
    title: 'Planes y Precios | Validea',
    description: 'Conocé nuestros planes flexibles para validar tus ideas. Desde emprendedores individuales hasta equipos empresariales.',
    type: 'product',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: 'Planes Validea',
      description: 'Planes de suscripción para la plataforma Validea',
      offers: {
        '@type': 'AggregateOffer',
        lowPrice: '0',
        highPrice: '99',
        priceCurrency: 'USD',
        offerCount: '3'
      }
    }
  },
  contact: {
    title: 'Contacto | Validea',
    description: 'Contactá con nuestro equipo para obtener más información sobre cómo Validea puede ayudarte a validar tus ideas.',
    type: 'contact',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      name: 'Contacto Validea',
      url: `${BASE_URL}/contact`
    }
  }
};

export const getPageMeta = (pageMeta?: Partial<MetaConfig>): MetaConfig => {
  if (pageMeta?.type && pageMeta.type in pageMetas) {
    return {
      ...defaultMeta,
      ...pageMetas[pageMeta.type as keyof typeof pageMetas],
      ...pageMeta
    };
  }
  return {
    ...defaultMeta,
    ...pageMeta
  };
}; 